import { useState, useEffect, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { itemsApi, categoriesApi, imagesApi } from '../lib/api';
import { assetUrl } from '../utils/assetUrl'
import { hasPhysicalAddress } from '../utils/locations';

const conditions = [
  { value: 'LIKE_NEW', label: 'Like New' },
  { value: 'GOOD', label: 'Good' },
  { value: 'FAIR', label: 'Fair' },
];

interface Photo {
  id: string;
  url: string;
  isDefault?: boolean;
}

export default function EditItem() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [categories, setCategories] = useState<string[]>([]);
  useEffect(() => {
    categoriesApi.getAll()
      .then((res) => setCategories(res.data.data.map((c: { name: string }) => c.name)))
      .catch(() => setCategories([]));
  }, []);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Power Tools',
    condition: 'GOOD',
    sku: '',
    serialNumber: '',
    price_daily: '',
    price_weekly: '',
    price_monthly: '',
    minRentalDays: '',
    pickupLocationLabel: '',
    weightLbs: '',
    lengthIn: '',
    widthIn: '',
    heightIn: '',
    securityDeposit: '',
  });
  const [requiresDeposit, setRequiresDeposit] = useState(false);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [defaultIndex, setDefaultIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const pickupLocations = user?.metadata?.locations?.pickup || [];
  // An item for rent can only be saved while the owner has a physical pickup address.
  const hasPickupAddress = hasPhysicalAddress(pickupLocations as any);

  useEffect(() => {
    if (id) {
      fetchItem(id);
    }
  }, [id]);

  const fetchItem = async (itemId: string) => {
    setIsLoading(true);
    try {
      const response = await itemsApi.getById(itemId);
      const data = response.data;

      if (!data.success || !data.data) {
        setNotFound(true);
        return;
      }

      const item = data.data;
      setFormData({
        title: item.title || '',
        description: item.description || '',
        category: item.category || 'Power Tools',
        condition: item.condition || 'GOOD',
        sku: item.sku || '',
        serialNumber: item.serialNumber || '',
        price_daily: item.dailyPrice ? String(parseFloat(item.dailyPrice)) : '',
        price_weekly: item.weeklyPrice ? String(parseFloat(item.weeklyPrice)) : '',
        price_monthly: item.monthlyPrice ? String(parseFloat(item.monthlyPrice)) : '',
        minRentalDays: item.minRentalDays ? String(item.minRentalDays) : '',
        pickupLocationLabel: item.pickupLocationLabel || '',
        weightLbs: item.weightLbs ? String(parseFloat(item.weightLbs)) : '',
        lengthIn: item.lengthIn ? String(parseFloat(item.lengthIn)) : '',
        widthIn: item.widthIn ? String(parseFloat(item.widthIn)) : '',
        heightIn: item.heightIn ? String(parseFloat(item.heightIn)) : '',
        securityDeposit: item.securityDeposit ? String(parseFloat(item.securityDeposit)) : '',
      });
      setRequiresDeposit(!!item.securityDeposit);

      const itemPhotos: Photo[] = (item.photos || []).map((p: any) => ({
        id: p.id,
        url: p.url,
        isDefault: p.isDefault || false,
      }));
      setPhotos(itemPhotos);

      const existingDefault = itemPhotos.findIndex((p) => p.isDefault);
      setDefaultIndex(existingDefault >= 0 ? existingDefault : 0);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('Failed to load listing');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const normalizePriceInput = (value: string): string => {
    let cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  };

  const formatPriceBlur = (value: string): string => {
    if (!value) return '';
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return value;
    return num.toFixed(2);
  };

  const PRICE_FIELDS = ['price_daily', 'price_weekly', 'price_monthly', 'securityDeposit'];

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (type === 'text' && PRICE_FIELDS.includes(name)) {
      setFormData({ ...formData, [name]: normalizePriceInput(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (PRICE_FIELDS.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: formatPriceBlur(value) }));
    }
  };

  // The server rotates the file and saves it under a new id and url, so the preview cannot
  // show a stale, cached orientation. Swap that new id and url in for the old photo; the
  // position and the default flag stay where they were.
  const handleRotate = async (index: number, degrees: 90 | 270) => {
    const photo = photos[index];
    if (!id || !photo || rotatingId) return;
    setRotatingId(photo.id);
    setError('');
    try {
      const response = await imagesApi.rotateItemImage(id, photo.id, degrees);
      const rotated = response.data.image;
      setPhotos((prev) =>
        prev.map((p) => (p.id === photo.id ? { ...p, id: rotated.id, url: rotated.url } : p))
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not rotate the photo. Please try again.');
    } finally {
      setRotatingId(null);
    }
  };

  const handleSetDefault = (index: number) => {
    setDefaultIndex(index);
    setPhotos((prev) =>
      prev.map((p, i) => ({ ...p, isDefault: i === index }))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setError('');
    setIsSaving(true);

    if (photos.length === 0) {
      setError('At least one photo is required');
      setIsSaving(false);
      return;
    }

    const updatedPhotos = photos.map((p, i) => ({
      id: p.id,
      url: p.url,
      isDefault: i === defaultIndex,
    }));

    try {
      const response = await itemsApi.update(id, {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        condition: formData.condition,
        sku: formData.sku || undefined,
        serialNumber: formData.serialNumber || undefined,
        dailyPrice: formData.price_daily ? parseFloat(formData.price_daily) : undefined,
        weeklyPrice: formData.price_weekly ? parseFloat(formData.price_weekly) : undefined,
        monthlyPrice: formData.price_monthly ? parseFloat(formData.price_monthly) : undefined,
        minRentalDays: formData.minRentalDays ? parseInt(formData.minRentalDays, 10) : null,
        pickupLocationLabel: formData.pickupLocationLabel || undefined,
        weightLbs: formData.weightLbs ? parseFloat(formData.weightLbs) : null,
        lengthIn: formData.lengthIn ? parseFloat(formData.lengthIn) : null,
        widthIn: formData.widthIn ? parseFloat(formData.widthIn) : null,
        heightIn: formData.heightIn ? parseFloat(formData.heightIn) : null,
        securityDeposit: requiresDeposit && formData.securityDeposit ? parseFloat(formData.securityDeposit) : null,
        photos: updatedPhotos,
      });

      if (!response.data.success) {
        setError(response.data.error || 'Failed to update listing');
        setIsSaving(false);
        return;
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to update listing');
      setIsSaving(false);
    }
  };

  const styles = {
    '--bg-primary': '#F7FAF7',
    '--forest-green': '#1B5E20',
    '--emerald': '#2E7D32',
    '--bg-tint': '#E8F5E9',
    '--slate': '#4A5568',
    '--slate-light': '#718096',
    '--border': '#E2E8F0',
    '--text-primary': '#1A202C',
    '--text-secondary': '#4A5568',
    '--text-muted': '#A0AEC0',
    '--error-bg': '#FED7D7',
    '--error-text': '#C53030',
    '--error-border': '#FC8181',
  } as React.CSSProperties;

  if (isLoading) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', ...styles}}>
        Loading listing...
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', ...styles}}>
        <p>Listing not found.</p>
        <Link to="/dashboard" style={{color: 'var(--forest-green)'}}>Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', ...styles}}>
      <header style={{ background: '#fff', borderBottom: '1px solid var(--border)', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, padding: '1rem 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(1rem, 4vw, 2rem)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to={user ? '/dashboard' : '/'} style={{display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none'}}>
            <img src="/IGotThat_logo_bk.svg" alt="I Got That U.S." width={230} height={34} style={{width: '230px', height: 'auto', display: 'block'}} />
          </Link>
          <Link to="/dashboard" style={{color: 'var(--slate)', textDecoration: 'none', fontSize: '0.9rem'}}>
            Back to Dashboard
          </Link>
        </div>
      </header>

      <main style={{paddingTop: '0', paddingBottom: '80px', background: 'var(--bg-primary)', minHeight: 'calc(100vh - 72px)'}}>
        <div style={{width: '100%', maxWidth: '640px', padding: '1rem', margin: '0 auto'}}>
          <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', border: '1px solid var(--border)', padding: 'clamp(1.5rem, 5vw, 2.5rem)' }}>
            <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
              <h1 style={{fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem 0'}}>Edit Listing</h1>
            </div>

            {error && (
              <div style={{marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'var(--error-bg)', color: 'var(--error-text)', fontSize: '0.875rem', border: '1px solid var(--error-border)'}}>
                {error}
              </div>
            )}

            {!hasPickupAddress && (
              <div role="alert" style={{display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.875rem 1rem', marginBottom: '1.25rem', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', color: '#92400E', fontSize: '0.875rem'}}>
                <span aria-hidden="true">&#9888;</span>
                <span>
                  You need a pickup address (street, city, state and ZIP) to keep this item available for rent. Add one, then save your changes.{' '}
                  <Link to="/profile?tab=locations" style={{fontWeight: 600, textDecoration: 'underline', color: 'inherit'}}>Add a pickup address</Link>
                </span>
              </div>
            )}
            <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: '1.25rem'}}>
              <div>
                <label htmlFor="title" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Item Name <span style={{color: 'var(--error-text)'}}>*</span>
                </label>
                <input id="title" type="text" name="title" value={formData.title} onChange={handleChange} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}} required />
              </div>

              <div>
                <label htmlFor="description" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>Description</label>
                <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={3} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', resize: 'vertical'}} />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '1rem'}} className="sm:grid-cols-2">
                <div>
                  <label htmlFor="category" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>Category <span style={{color: 'var(--error-text)'}}>*</span></label>
                  <select id="category" name="category" value={formData.category} onChange={handleChange} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white'}} required>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="condition" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>Condition <span style={{color: 'var(--error-text)'}}>*</span></label>
                  <select id="condition" name="condition" value={formData.condition} onChange={handleChange} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white'}} required>
                    {conditions.map(cond => <option key={cond.value} value={cond.value}>{cond.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Pickup Location Selector */}
              <div>
                <label htmlFor="pickupLocationLabel" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Pickup Location <span style={{color: 'var(--error-text)'}}>*</span>
                </label>
                {pickupLocations.length > 0 ? (
                  <select
                    id="pickupLocationLabel"
                    name="pickupLocationLabel"
                    value={formData.pickupLocationLabel}
                    onChange={handleChange}
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white'}}
                  >
                    <option value="">No pickup location selected</option>
                    {pickupLocations.map(loc => (
                      <option key={loc.id} value={loc.name}>
                        {loc.name}{loc.address ? ` — ${loc.address}` : ''}{loc.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p style={{fontSize: '0.875rem', color: 'var(--text-muted)', padding: '0.75rem', background: 'var(--bg-tint)', borderRadius: '8px'}}>
                    No pickup locations saved.{' '}
                    <Link to="/profile" style={{color: 'var(--forest-green)', fontWeight: 500}}>Add one in Profile &gt; Locations</Link>.
                  </p>
                )}
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '1rem'}} className="sm:grid-cols-2">
                <div>
                  <label htmlFor="sku" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>SKU / Model Number</label>
                  <input id="sku" type="text" name="sku" value={formData.sku} onChange={handleChange} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}} />
                </div>
                <div>
                  <label htmlFor="serialNumber" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>Serial Number</label>
                  <input id="serialNumber" type="text" name="serialNumber" value={formData.serialNumber} onChange={handleChange} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}} />
                </div>
              </div>

              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.5rem'}}>Pricing <span style={{color: 'var(--error-text)'}}>*</span></label>
                <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem'}} className="sm:grid-cols-3">
                  <div>
                    <label htmlFor="price_daily" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>Daily ($)</label>
                    <input id="price_daily" type="text" inputMode="decimal" name="price_daily" value={formData.price_daily} onChange={handleChange} onBlur={handleBlur} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}} required />
                  </div>
                  <div>
                    <label htmlFor="price_weekly" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>Weekly ($)</label>
                    <input id="price_weekly" type="text" inputMode="decimal" name="price_weekly" value={formData.price_weekly} onChange={handleChange} onBlur={handleBlur} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}} />
                  </div>
                  <div>
                    <label htmlFor="price_monthly" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>Monthly ($)</label>
                    <input id="price_monthly" type="text" inputMode="decimal" name="price_monthly" value={formData.price_monthly} onChange={handleChange} onBlur={handleBlur} style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}} />
                  </div>
                </div>
              </div>

              {/* Minimum Rental Duration */}
              <div>
                <label htmlFor="minRentalDays" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Minimum Rental (days)
                </label>
                <input
                  id="minRentalDays"
                  type="number"
                  name="minRentalDays"
                  value={formData.minRentalDays}
                  onChange={handleChange}
                  min="1"
                  max="365"
                  style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                  placeholder="Leave blank for no minimum"
                />
                <p style={{fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem'}}>
                  Require renters to keep the item for at least this many days. Leave blank to allow 1-day rentals.
                </p>
              </div>

              {/* Security Deposit */}
              <div>
                <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={requiresDeposit}
                    onChange={(e) => {
                      setRequiresDeposit(e.target.checked);
                      if (!e.target.checked) {
                        setFormData(prev => ({ ...prev, securityDeposit: '' }));
                      }
                    }}
                    style={{width: '1.1rem', height: '1.1rem', accentColor: 'var(--forest-green)'}}
                  />
                  This item requires a deposit
                </label>
                {requiresDeposit && (
                  <div style={{marginTop: '0.5rem'}}>
                    <label htmlFor="securityDeposit" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>
                      Deposit amount ($)
                    </label>
                    <input
                      id="securityDeposit"
                      type="text"
                      inputMode="decimal"
                      name="securityDeposit"
                      value={formData.securityDeposit}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                      placeholder="e.g., 50.00"
                    />
                    <p style={{fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem'}}>
                      Refundable amount collected upfront and shown to renters as a separate line item.
                    </p>
                  </div>
                )}
              </div>

              {/* Weight & Dimensions */}
              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Weight & Dimensions (optional)
                </label>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem'}}>
                  <input
                    id="weightLbs"
                    type="number"
                    inputMode="decimal"
                    name="weightLbs"
                    value={formData.weightLbs}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Weight (lbs)"
                  />
                  <input
                    id="lengthIn"
                    type="number"
                    inputMode="decimal"
                    name="lengthIn"
                    value={formData.lengthIn}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Length (in)"
                  />
                  <input
                    id="widthIn"
                    type="number"
                    inputMode="decimal"
                    name="widthIn"
                    value={formData.widthIn}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Width (in)"
                  />
                  <input
                    id="heightIn"
                    type="number"
                    inputMode="decimal"
                    name="heightIn"
                    value={formData.heightIn}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Height (in)"
                  />
                </div>
                <p style={{fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem'}}>
                  Used to estimate shipping cost and check DoorDash eligibility. Leave blank if unknown.
                </p>
              </div>

              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.75rem'}}>
                  Photos — click "Set default" to choose the thumbnail, or use the arrows to turn a photo that is sideways
                </label>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem'}} className="sm:grid-cols-3 md:grid-cols-5">
                  {photos.map((photo, index) => (
                    <div
                      key={photo.id}
                      style={{
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: index === defaultIndex ? '3px solid var(--forest-green)' : '2px solid var(--border)',
                        aspectRatio: '1'
                      }}
                    >
                      <img
                        src={assetUrl(photo.url)}
                        alt={`Photo ${index + 1}`}
                        style={{width: '100%', height: '100%', objectFit: 'cover', opacity: rotatingId === photo.id ? 0.4 : 1}}
                      />
                      {([
                        { degrees: 270 as const, side: 'left', label: 'left', path: 'M3 12a9 9 0 1 0 3-6.7L3 8', head: '3 3 3 8 8 8' },
                        { degrees: 90 as const, side: 'right', label: 'right', path: 'M21 12a9 9 0 1 1-3-6.7L21 8', head: '21 3 21 8 16 8' },
                      ]).map((r) => (
                        <button
                          key={r.degrees}
                          type="button"
                          onClick={() => handleRotate(index, r.degrees)}
                          disabled={!!rotatingId}
                          aria-label={`Rotate photo ${index + 1} ${r.label}`}
                          title={`Rotate ${r.label}`}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            [r.side]: '4px',
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.55)',
                            color: 'white',
                            border: 'none',
                            cursor: rotatingId ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d={r.path} />
                            <polyline points={r.head} />
                          </svg>
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleSetDefault(index)}
                        style={{
                          position: 'absolute',
                          bottom: '4px',
                          left: '4px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: index === defaultIndex ? 'var(--forest-green)' : 'rgba(0,0,0,0.5)',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                        }}
                      >
                        {index === defaultIndex ? 'Default' : 'Set default'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{display: 'flex', gap: '1rem', marginTop: '0.5rem'}} className="flex-col sm:flex-row">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  disabled={isSaving}
                  style={{flex: 1, padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'white', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '1rem', cursor: isSaving ? 'not-allowed' : 'pointer'}}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !!rotatingId || !hasPickupAddress}
                  style={{flex: 1, padding: '0.75rem 1rem', background: 'var(--forest-green)', color: 'white', fontWeight: 600, fontSize: '1rem', borderRadius: '8px', border: 'none', cursor: isSaving || rotatingId || !hasPickupAddress ? 'not-allowed' : 'pointer', opacity: isSaving || rotatingId || !hasPickupAddress ? 0.5 : 1}}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
